import {Injectable} from '@angular/core';
import {environment} from '@env';
import {ANALYTICS_ACTION, ANALYTICS_COMPONENT, AnalyticsParams, ANALYTICS_RESULT} from '@shared/analytics';
import {logEvent, setUserProperties, isSupported, getAnalytics} from '@angular/fire/analytics';
import {LoggerService} from '@app/service/logger/logger.service';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  enabled = true;

  constructor(private logger: LoggerService) {
    this.enabled = !environment.local;
  }

  /**
   * Track an analytical event by the event name and pass in optional parameters like store state for debugging
   * @param eventName
   * @param params
   */
  async trackEvent(eventName: string, params?: AnalyticsParams): Promise<void> {
    if (this.enabled && await isSupported()) {
      const analytics = getAnalytics();
      return logEvent(analytics, eventName, params as any);
    }
  }

  buildEventName(eventName: string, component: ANALYTICS_COMPONENT | string, subComponent: string,
                 action: ANALYTICS_ACTION, result: ANALYTICS_RESULT) {
    if (component) {
      eventName += `_${component}`;
    }
    if (subComponent) {
      eventName += `_${subComponent}`;
    }
    if (action) {
      eventName += `_${action}`;
    }
    if (result) {
      eventName += `_${result}`;
    }
    if (eventName.length > 40) {
      this.logger.error(`EVENT NAME - ${eventName} is too long, removing subComponent`);
      return this.buildEventName('00', component, '', action, result);
    }
    return eventName;
  }

  /**
   * 00 - Mission Critical Analytics E.g Payment Errors, Report Create Error
   * @param component
   * @param subComponent
   * @param action
   * @param result
   * @param params
   */
  trackMissionCritical(component: ANALYTICS_COMPONENT, subComponent: string, action: ANALYTICS_ACTION,
                       result: ANALYTICS_RESULT, params?: AnalyticsParams) {
    const eventName = this.buildEventName('00', component, subComponent, action, result);
    return this.trackEvent(eventName, params);
  }

  /**
   * 01 - Business Use Case Analytics E.g Account Signups, Free Report, Paid Report
   * @param component
   * @param subComponent
   * @param action
   * @param result
   * @param params
   */
  trackBusinessMetric(component: ANALYTICS_COMPONENT, subComponent: string, action: ANALYTICS_ACTION,
                      result: ANALYTICS_RESULT, params?: AnalyticsParams) {
    const eventName = this.buildEventName('01', component, subComponent, action, result);
    return this.trackEvent(eventName, params);
  }

  /**
   * 02 - UX Flow Analytics E.g Button Clicks, Using certain inputs
   * @param component
   * @param subComponent
   * @param action
   * @param result
   * @param params
   */
  trackUXMetric(component: ANALYTICS_COMPONENT | string, subComponent: string, action: ANALYTICS_ACTION,
                result: ANALYTICS_RESULT, params?: AnalyticsParams) {
    const eventName = this.buildEventName('02', component, subComponent, action, result);
    return this.trackEvent(eventName, params);
  }

  /**
   * 03 - UI Visit Analytics E.g Page Visits, Article Reads
   * @param component
   * @param subComponent
   * @param action
   * @param result
   * @param params
   */
  trackUIVisitMetric(component: ANALYTICS_COMPONENT, subComponent: string, action: ANALYTICS_ACTION,
                     result: ANALYTICS_RESULT, params?: AnalyticsParams) {
    const eventName = this.buildEventName('03', component, subComponent, action, result);
    return this.trackEvent(eventName, params);
  }

  /**
   * 04 - Share Lead Generation E.g Sharing Reports, Sharing Blogs
   * @param component
   * @param subComponent
   * @param action
   * @param result
   * @param params
   */
  trackShareLeads(component: ANALYTICS_COMPONENT | string, subComponent: string, action: ANALYTICS_ACTION,
                  result: ANALYTICS_RESULT, params?: AnalyticsParams) {
    const eventName = this.buildEventName('04', component, subComponent, action, result);
    return this.trackEvent(eventName, params);
  }

  /**
   * 05 - Outbound Link Click E.g Going to an external page in blog, report, vehicle view
   * @param component
   * @param subComponent
   * @param action
   * @param result
   * @param params
   */
  trackOutboundMetric(component: ANALYTICS_COMPONENT | string, subComponent: string, action: ANALYTICS_ACTION,
                      result: ANALYTICS_RESULT, params?: AnalyticsParams) {
    const eventName = this.buildEventName('05', component, subComponent, action, result);
    return this.trackEvent(eventName, params);
  }

  /**
   * 06 - Track API calls to get a sense of where calls are going too
   * @param component
   * @param subComponent
   * @param action
   * @param result
   * @param params
   */
  trackAPICallsMetric(component: ANALYTICS_COMPONENT | string, subComponent: string, action: ANALYTICS_ACTION,
                      result: ANALYTICS_RESULT, params?: AnalyticsParams) {
    const eventName = this.buildEventName('06', component, subComponent, action, result);
    return this.trackEvent(eventName, params);
  }

  /**
   * Set user properties for an app session
   * @param properties
   */
  async setUserProperties(properties: any) {
    if (this.enabled && await isSupported()) {
      const analytics = getAnalytics();
      return setUserProperties(analytics, properties);
    }
  }
}
